import React from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Field } from 'redux-form';

import FormField from './form-field';
import MoneyField from './money-field';
import Dropdown from './dropdown';
import Purchase from '../lib/purchase';

import SplitPurchaseProjection from '../containers/split-purchase-projection.container';
import { EVEN_SPLIT, SPECIFIED_PER_PERSON } from '../lib/split-types';
import { formatMessage } from '../../lib/intl';
import { getURLParams } from '../../lib/functions';

import '../css/split-purchase-settings.css';

const i18n = defineMessages({
  evenSplitOptionText: {
    id: 'splitPurchase.totalCost.dropdownOptions.evenSplit.text',
    defaultMessage: 'evenly',
  },
  evenSplitOptionHint: {
    id: 'splitPurchase.totalCost.dropdownOptions.evenSplit.hint',
    defaultMessage: 'each pays the same',
  },
  sppOptionText: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.text',
    defaultMessage: 'custom',
  },
  sppOptionHint: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint',
    defaultMessage: 'vary amounts per payer',
  },

  slotsText: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText',
    defaultMessage: '{count, plural, one {slot} other {slots}}',
  },
  slotsText_share: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_share',
    defaultMessage: '{count, plural, one {share} other {shares}}',
  },
  slotsText_adult: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_adult',
    defaultMessage: '{count, plural, one {adult} other {adults}}',
  },
  slotsText_passenger: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_passenger',
    defaultMessage: '{count, plural, one {passenger} other {passengers}}',
  },
  slotsText_ticket: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_ticket',
    defaultMessage: '{count, plural, one {ticket} other {tickets}}',
  },
  slotsText_seat: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_seat',
    defaultMessage: '{count, plural, one {seat} other {seats}}',
  },
  slotsText_item: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_item',
    defaultMessage: '{count, plural, one {item} other {items}}',
  },
  slotsText_shirt: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_shirt',
    defaultMessage: '{count, plural, one {shirt} other {shirts}}',
  },
  slotsText_person: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_person',
    defaultMessage: '{count, plural, one {person} other {people}}',
  },
  slotsText_guest: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_guest',
    defaultMessage: '{count, plural, one {guest} other {guests}}',
  },
  slotsText_player: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_player',
    defaultMessage: '{count, plural, one {player} other {players}}',
  },
  slotsText_student: {
    id: 'splitPurchase.totalCost.dropdownOptions.spp.hint.slotsText_student',
    defaultMessage: '{count, plural, one {student} other {students}}',
  },
});

export class SplitPurchaseTotalCost extends React.Component {
  constructor(props) {
    super(props);

    this.formatMessage = formatMessage(this.props.intl);

    const qsFormValues = getURLParams();

    this.state = {
      splitType: qsFormValues.splitType || props.defaultSplitType,
      setMaxSlots: props.groupMaxSlots,
    };

    this.onSplitTypeChange = this.onSplitTypeChange.bind(this);
    this.onSetMaxSlotsChange = this.onSetMaxSlotsChange.bind(this);
  }

  onSplitTypeChange(ev) {
    const { onSplitTypeChange } = this.props;
    this.setState({ splitType: ev.target.value });
    onSplitTypeChange(ev.target.value);
  }

  onSetMaxSlotsChange(ev) {
    this.setState({ setMaxSlots: ev.target.checked });
  }

  getCurrency() {
    return this.props.currencyCode;
  }

  showMaxSlotsInput() {
    if (!this.props.showMaxSlots) return false;
    return this.state.setMaxSlots;
  }

  isSpecifiedPerPerson() {
    return Purchase.isSpecifiedPerPerson(this.state.splitType);
  }

  renderSplitDropdown() {
    const dropdownOptions = [
      {
        value: EVEN_SPLIT,
        text: this.formatMessage(i18n.evenSplitOptionText),
        hint: this.formatMessage(i18n.evenSplitOptionHint),
      },
      {
        value: SPECIFIED_PER_PERSON,
        text: this.formatMessage(i18n.sppOptionText),
        hint: this.formatMessage(i18n.sppOptionHint),
      },
    ];

    return (
      <div className="inline-part">
        <Field
          name="splitType"
          component={Dropdown}
          options={dropdownOptions}
          onChange={this.onSplitTypeChange}
          noGroup
        />
      </div>
    );
  }

  renderMinContribution() {
    const { currencyCode } = this.props;

    return (
      <div>
        <span className="mr-1 ml-1 inline-part">
          <FormattedMessage
            id="splitPurchase.totalCost.forDifferentAmounts"
            defaultMessage="for different amounts per contributor."
          />
        </span>
        <div className="inline-part d-none">
          <MoneyField
            name="minContribution"
            className="min-contribution"
            label={
              <FormattedMessage
                id="splitPurchase.total.minContribution"
                defaultMessage="Min. Contribution"
              />
            }
            component={FormField}
            currency={currencyCode}
            onChange={this.props.onMinContributionChange}
            noGroup
          />
        </div>
      </div>
    );
  }

  renderMinSlots() {
    const fieldProps = {
      name: 'minSlots',
      type: 'number',
      component: FormField,
      min: this.props.minSlots || 2,
      max: this.props.maxSlots || 99,
      normalize: (value) => {
        if (value === '') return this.props.minSlots === 1 ? 2 : this.props.minSlots;
        return parseInt(value, 10);
      },
      onChange: ev => this.props.onMinSlotsChange(ev.target.value),
    };

    return (
      <div>
        <span className="mr-1 ml-1 inline-part">
          <FormattedMessage
            id="splitPurchase.minSlots.atLeast"
            defaultMessage="across"
          />
        </span>
        <div className="inline-part">
          <Field {...fieldProps} />
        </div>
        <span className="mr-1 ml-1 inline-part">
          {this.formatMessage(i18n.slotsText, { count: this.props.selectedSlots }, this.props.slotName)}
        </span>
      </div>
    );
  }

  renderMaxSlots() {
    if (!this.showMaxSlotsInput()) return null;

    const fieldProps = {
      name: 'maxSlots',
      type: 'number',
      min: this.props.minSlots,
      max: this.props.maxSlots || 99,
      component: FormField,
      normalize: (value) => {
        if (value === '') return 0;
        return parseInt(value, 10);
      },
      onChange: ev => this.props.onMaxSlotsChange(ev.target.value),
    };

    return (
      <div>
        <span className="mr-1 ml-1 inline-part">
          <FormattedMessage
            id="splitPurchase.totalCost.maxSlots"
            defaultMessage="to a maximum of"
          />
        </span>
        <div className="inline-part">
          <Field {...fieldProps} />
        </div>
      </div>
    );
  }

  renderSetMaxSlotsCheckbox() {
    if (!this.props.showMaxSlots) return null;

    return (
      <div className="mt-3">
        <Field
          name="setMaxSlots"
          type="checkbox"
          component={FormField}
          checkbox="true"
          label={
            <FormattedMessage
              id="splitPurchase.totalCost.maxSlotsField.label"
              defaultMessage="Set group maximum."
            />
          }
          onChange={this.onSetMaxSlotsChange}
        />
      </div>
    );
  }

  renderSlots() {
    return (
      <div>
        <div className="form-inline form-mixed h3 mb-0">
          <div className="inline-part">
            <FormattedMessage
              id="splitPurchase.totalCost.slots.split"
              defaultMessage="Split"
            />
          </div>
          {this.renderSplitDropdown()}
          {this.renderMinSlots()}
          {this.renderMaxSlots()}
        </div>
        {this.renderSetMaxSlotsCheckbox()}
        <SplitPurchaseProjection namespace={this.props.namespace} />
      </div>
    );
  }

  renderShare() {
    return (
      <div>
        <div className="form-inline form-mixed h3 mb-0">
          <div className="inline-part">
            <FormattedMessage
              id="splitPurchase.totalCost.share.split"
              defaultMessage="Split"
            />
          </div>
          {this.renderSplitDropdown()}
          {this.renderMinContribution()}
        </div>
        <p className="share-info">
          <FormattedMessage
            id="splitPurchase.totalCost.editMessage"
            defaultMessage="Edit how you split at any time."
          />
        </p>
      </div>
    );
  }

  render() {
    const controls = this.isSpecifiedPerPerson() ?
      this.renderShare() : this.renderSlots();

    return (
      <div className="split-purchase-settings split-purchase-total-cost">
        {controls}
      </div>
    );
  }
}

SplitPurchaseTotalCost.defaultProps = {
  splitType: EVEN_SPLIT,
  slotName: 'share',
  onSplitTypeChange: () => { },
  onMinContributionChange: () => { },
  onMinSlotsChange: () => { },
  onMaxSlotsChange: () => { },
};

export default injectIntl(SplitPurchaseTotalCost);



// WEBPACK FOOTER //
// ./src/desktop/components/split-purchase.total-cost.js